import { getMomentTz, MesfModal } from "@dexteel/mesf-core";
import { ReportProblemOutlined } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Grid2,
	Stack,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation } from "@tanstack/react-query";
import type { GridApi } from "ag-grid-community";
import type { Moment } from "moment";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { isGaugeSpecItem } from "@/shared/utils/sectionSpecUnits";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import type {
	SectionSpec,
	SectionSpecDetail,
} from "@/shared/models/SectionSpec";
import type { SectionSpecUpload } from "@/shared/models/SectionSpecUpload";
import {
	correctSectionSpec,
	uploadSectionSpecImage,
	upsertSectionSpec,
} from "@/shared/repositories/SectionSpecRepository";
import {
	SECTION_SPEC_FILE_KIND,
	type SectionSpecFileKind,
} from "@/shared/models/SectionSpec";
import { getSectionSpecCategoryTabs } from "../../models/SectionSpecCategoryTabs";
import {
	replaceSectionSpecFileChange,
	useCanReplaceSectionSpecFile,
} from "../../hooks/useSectionSpecFileRotation";
import {
	getSectionSpecItemError,
	sectionSpecDraftResolver,
	type SectionSpecDraftFormValues,
	type SectionSpecValidationContext,
	type SpecBandField,
	type SpecItemDraftRow,
} from "../../models/SectionSpecForm";
import {
	parseSectionSpecNumber,
	toItemPayload,
	toSectionSpecFormValues,
} from "../../models/SectionSpecFormat";
import type { SectionSpecFileChange } from "../../models/SectionSpecRotation";
import { hasSectionSpecSubRevisions } from "../../models/SectionSpecState";
import { SectionSpecDimensionsGrid } from "./SectionSpecDimensionsGrid";
import { ControlledSpecField } from "./SectionSpecFields";
import { SectionSpecFileSelection } from "./SectionSpecFileSelection";

const moment = getMomentTz();

export type SectionSpecEditorMode = "create" | "draft" | "correction";

interface SectionSpecFormProps {
	mode: SectionSpecEditorMode;
	sectionId: number;
	assetId: number;
	profileSpecId: number | null;
	source: SectionSpecDetail;
	sectionDetail: SectionSpecDetail;
	onClose: () => void;
	onSaved: (profileSpecId: number) => void;
	onError: (error: Error) => void;
}

const uploadSectionSpecFile = async (
	file: File,
	label: string,
): Promise<SectionSpecUpload> => {
	try {
		return await uploadSectionSpecImage(file);
	} catch (error) {
		const message = error instanceof Error ? error.message : "Unknown error";
		throw new Error(
			`${label} upload failed: ${message}. The specification was not saved.`,
		);
	}
};

const uploadFile = async (
	file: File | null,
	label: string,
): Promise<SectionSpecUpload | null> =>
	file === null ? null : uploadSectionSpecFile(file, label);

export const SectionSpecForm = ({
	mode,
	sectionId,
	assetId,
	profileSpecId,
	source,
	sectionDetail,
	onClose,
	onSaved,
	onError,
}: SectionSpecFormProps) => {
	const isM1 = assetId === MILL_1.id;
	const canReplaceFile = useCanReplaceSectionSpecFile();
	const [initialSpec] = useState(() => source.spec);
	const [drawingChange, setDrawingChange] =
		useState<SectionSpecFileChange | null>(null);
	const [referenceChange, setReferenceChange] =
		useState<SectionSpecFileChange | null>(null);
	const [revision, setRevision] = useState(
		initialSpec?.Rev == null ? "" : String(initialSpec.Rev),
	);
	const [effectiveDate, setEffectiveDate] = useState<Moment | null>(null);
	const [pendingCorrection, setPendingCorrection] =
		useState<SectionSpecDraftFormValues | null>(null);
	const gridApiRef = useRef<GridApi<SpecItemDraftRow> | null>(null);
	const [defaultValues] = useState(() => {
		return toSectionSpecFormValues(source, {
			create: mode === "create",
		});
	});
	const validationContext: SectionSpecValidationContext = {
		initialValues: defaultValues,
		initialItems: defaultValues.items,
	};

	const {
		control,
		formState: { errors, isDirty },
		getValues,
		handleSubmit,
		setValue,
		watch,
	} = useForm<SectionSpecDraftFormValues, SectionSpecValidationContext>({
		defaultValues,
		context: validationContext,
		mode: "onChange",
		reValidateMode: "onChange",
		resolver: sectionSpecDraftResolver,
	});

	const rows = watch("items");
	const itemError = errors.items
		? getSectionSpecItemError(rows, validationContext)
		: null;

	const isCorrection = mode === "correction";
	const useCategoryTabs = isM1;
	const categoryTabs = useCategoryTabs ? getSectionSpecCategoryTabs(rows) : [];
	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
		null,
	);
	const activeCategoryId = categoryTabs.some(
		(tab) => tab.id === selectedCategoryId,
	)
		? selectedCategoryId
		: categoryTabs[0]?.id;
	const visibleRows =
		useCategoryTabs && activeCategoryId != null
			? rows.filter((row) => row.ProfileCategoryId === activeCategoryId)
			: rows;
	const liveSpec = source.spec;
	const inspectionCount = liveSpec?.InspectionCount ?? 0;
	const revisionLocked =
		isCorrection &&
		(inspectionCount > 0 ||
			(profileSpecId !== null &&
				hasSectionSpecSubRevisions(sectionDetail, profileSpecId)));
	const measuredCounts = new Map(
		source.items.map((item) => [
			item.ProfileItemId,
			item.MeasuredInspectionCount ?? 0,
		]),
	);
	const highestOtherRev = Math.max(
		0,
		...sectionDetail.versions
			.filter((version) => version.ProfileSpecId !== profileSpecId)
			.map((version) => version.Rev ?? 0),
	);
	const revisionChanged = revision !== String(liveSpec?.Rev ?? "");
	const revisionError =
		!isCorrection || revisionLocked || !revisionChanged
			? null
			: !/^\d+$/.test(revision) || !Number.isSafeInteger(Number(revision))
				? "Revision Number must be a whole number"
				: Number(revision) <= highestOtherRev
					? `Revision Number must be above ${highestOtherRev}`
					: null;
	const effectiveDateError =
		isCorrection &&
		!revisionLocked &&
		effectiveDate !== null &&
		!effectiveDate.isValid()
			? "A valid Effective Date is required"
			: null;
	const displayedEffectiveDate =
		revisionLocked || effectiveDate === null
			? liveSpec?.EffectiveDate
				? moment(moment.utc(liveSpec.EffectiveDate).valueOf())
				: null
			: effectiveDate;
	const revisionLockReason = revisionLocked
		? inspectionCount > 0
			? `This revision has ${inspectionCount} inspection(s); its revision number and effective date cannot be changed.`
			: "This revision has sub-revisions; its revision number and effective date cannot be changed."
		: null;
	const revisionLockAdornment = revisionLockReason ? (
		<Tooltip title={revisionLockReason}>
			<Box
				component="span"
				tabIndex={0}
				sx={{ display: "inline-flex", color: "warning.main", mr: 1 }}
			>
				<ReportProblemOutlined fontSize="small" />
			</Box>
		</Tooltip>
	) : undefined;

	const fileChanges: Array<{
		change: SectionSpecFileChange | null;
		kind: SectionSpecFileKind;
		label: string;
	}> = [
		{
			change: drawingChange,
			kind: SECTION_SPEC_FILE_KIND.MILL_DRAWING,
			label: "Mill Drawing",
		},
		{
			change: referenceChange,
			kind: SECTION_SPEC_FILE_KIND.DIMENSIONS_REFERENCE,
			label: "Dimensions Reference",
		},
	];
	const pendingFileChanges = fileChanges.flatMap(({ change, ...file }) =>
		change === null ? [] : [{ ...file, change }],
	);

	const isFileOnlyCorrection =
		isCorrection &&
		canReplaceFile &&
		!isDirty &&
		!revisionChanged &&
		effectiveDate === null &&
		pendingFileChanges.length > 0;

	const saveFiles = async (spec: SectionSpec) => {
		const uploads = await Promise.all(
			pendingFileChanges.map(({ change, label }) =>
				uploadSectionSpecFile(change.file, label),
			),
		);
		for (const [index, { change, kind }] of pendingFileChanges.entries())
			await replaceSectionSpecFileChange(
				spec,
				kind,
				change,
				uploads[index].uploadFileId,
			);
		return spec.ProfileSpecId;
	};

	const saveMutation = useMutation({
		mutationFn: async (values: SectionSpecDraftFormValues) => {
			if (isFileOnlyCorrection && initialSpec) return saveFiles(initialSpec);
			const [drawingUpload, referenceUpload] = await Promise.all([
				uploadFile(drawingChange?.file ?? null, "Mill Drawing"),
				uploadFile(referenceChange?.file ?? null, "Dimensions Reference"),
			]);
			const specValues = {
				printNo: values.printNo || null,
				area: parseSectionSpecNumber(values.area),
				targetLbsPerFoot: parseSectionSpecNumber(values.target),
				minLbsPerFoot: parseSectionSpecNumber(values.min),
				maxLbsPerFoot: parseSectionSpecNumber(values.max),
				weightProfileItemId: source.weightProfileItemId,
				legDeltaMax: parseSectionSpecNumber(values.legDelta),
				// A new upload replaces the file; otherwise keep the current one.
				millDrawingUploadFileId:
					drawingUpload?.uploadFileId ??
					initialSpec?.MillDrawingUploadFileId ??
					null,
				dimensionsReferenceUploadFileId:
					referenceUpload?.uploadFileId ??
					initialSpec?.DimensionsReferenceUploadFileId ??
					null,
				comments: initialSpec?.Comments ?? null,
				items: toItemPayload(values.items),
			};
			if (!isCorrection)
				return upsertSectionSpec({
					...specValues,
					profileSpecId: mode === "draft" ? profileSpecId : null,
					sectionId,
					assetId,
				});
			if (profileSpecId === null)
				throw new Error("A published revision is required.");
			return correctSectionSpec({
				...specValues,
				profileSpecId,
				rev: revisionLocked || !revisionChanged ? null : Number(revision),
				effectiveDate:
					revisionLocked || effectiveDate === null
						? null
						: effectiveDate.toISOString(),
			});
		},
		onSuccess: onSaved,
		onError,
	});
	const isSaving = saveMutation.isPending;

	const updateRow = (
		profileItemId: number,
		change: (row: SpecItemDraftRow) => SpecItemDraftRow,
	) =>
		setValue(
			"items",
			getValues("items").map((row) =>
				row.ProfileItemId === profileItemId ? change(row) : row,
			),
			{ shouldDirty: true, shouldValidate: true },
		);

	const submit = () => {
		if (isSaving) return;
		const gridApi = gridApiRef.current;
		if (gridApi && !gridApi.isDestroyed()) gridApi.stopEditing();
		if (revisionError || effectiveDateError) return;
		void handleSubmit(
			(values) =>
				isCorrection && !isFileOnlyCorrection
					? setPendingCorrection(values)
					: saveMutation.mutate(values),
			(validationErrors) => {
				if (!useCategoryTabs) return;
				const categoryId = getValues("items").find(
					(_, index) =>
						Object.keys(validationErrors.items?.[index] ?? {}).length > 0,
				)?.ProfileCategoryId;
				if (categoryId !== undefined) setSelectedCategoryId(categoryId);
			},
		)();
	};

	return (
		<>
			<MesfModal.Content
				data-test-id="section-spec-form"
				dividers
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "75dvh",
					minHeight: 0,
					overflowY: { xs: "auto", md: "hidden" },
					"@media (max-height: 600px)": { overflowY: "auto" },
					minWidth: 0,
					width: (theme) => theme.breakpoints.values.xl,
					maxWidth: "100%",
				}}
			>
				<Stack
					spacing={1.5}
					sx={{
						pt: 1,
						minHeight: 0,
						flex: { md: 1 },
						"@media (max-height: 600px)": { flex: "none" },
					}}
				>
					{isCorrection && (
						<>
							<Alert severity="warning">
								{inspectionCount > 0
									? `This published revision has ${inspectionCount} associated inspection(s). Saving recalculates their results with the new values. Its revision number and effective date cannot be changed, and dimensions measured by its inspections cannot be removed.`
									: revisionLocked
										? "This published revision has sub-revisions. Its revision number and effective date cannot be changed."
										: "This published revision has no inspections. Its revision number and effective date can still be changed."}
							</Alert>
							<Grid2 container spacing={1} size="auto">
								<Grid2 size={{ xs: 12, sm: 4 }}>
									<TextField
										label="Revision Number"
										size="small"
										fullWidth
										required
										disabled={isSaving || revisionLocked}
										value={
											revisionLocked ? String(liveSpec?.Rev ?? "") : revision
										}
										error={Boolean(revisionError)}
										helperText={revisionError ?? undefined}
										slotProps={{
											htmlInput: {
												inputMode: "numeric",
												"data-test-id": "spec-revision-input",
											},
											input: { startAdornment: revisionLockAdornment },
										}}
										onChange={(event) => setRevision(event.target.value)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, sm: 4 }}>
									<DateTimePicker
										label="Effective Date"
										value={displayedEffectiveDate}
										onChange={(value) =>
											setEffectiveDate(value?.clone().startOf("minute") ?? null)
										}
										format="MM/DD/YYYY HH:mm"
										views={["year", "month", "day", "hours", "minutes"]}
										timeSteps={{ minutes: 1 }}
										ampm={false}
										disabled={isSaving || revisionLocked}
										slotProps={{
											textField: {
												inputProps: {
													"data-test-id": "spec-effective-date-input",
												},
												fullWidth: true,
												required: true,
												size: "small",
												error: Boolean(effectiveDateError),
												helperText: effectiveDateError ?? undefined,
												InputProps: { startAdornment: revisionLockAdornment },
											},
										}}
									/>
								</Grid2>
							</Grid2>
						</>
					)}
					<Grid2
						container
						spacing={1}
						columns={{ xs: 12, md: isM1 ? 20 : 12 }}
						size="auto"
					>
						<ControlledSpecField
							control={control}
							disabled={isSaving}
							field="printNo"
							label="Print No."
						/>
						<ControlledSpecField
							control={control}
							disabled={isSaving}
							field="area"
							label="Area (sq.in.)"
						/>
						{!isM1 && (
							<ControlledSpecField
								control={control}
								disabled={isSaving}
								field="legDelta"
								label="Leg delta max"
							/>
						)}
						<ControlledSpecField
							control={control}
							disabled={isSaving}
							field="min"
							label="Min lb/ft"
						/>
						<ControlledSpecField
							control={control}
							disabled={isSaving}
							field="target"
							label="Target lb/ft"
						/>
						<ControlledSpecField
							control={control}
							disabled={isSaving}
							field="max"
							label="Max lb/ft"
						/>
					</Grid2>
					<Grid2
						container
						spacing={1.5}
						alignItems="stretch"
						sx={{
							minHeight: 0,
							flex: { md: 1 },
							"@media (max-height: 600px)": { flex: "none" },
						}}
					>
						<Grid2
							size={{ xs: 12, md: 6 }}
							sx={{ display: "flex", minHeight: 0 }}
						>
							<Stack spacing={0.75} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
								<Typography variant="subtitle2" fontWeight={700}>
									Dimensions
								</Typography>
								{itemError && (
									<Alert
										severity="error"
										role="alert"
										data-test-id="spec-dimension-errors"
										sx={{ flexShrink: 0, maxHeight: 160, overflowY: "auto" }}
									>
										Correct these dimension values before saving:
										<Box
											component="ul"
											sx={{ m: 0, pl: 2, overflowWrap: "anywhere" }}
										>
											{itemError.split("\n").map((message) => (
												<li key={message}>{message}</li>
											))}
										</Box>
									</Alert>
								)}
								{useCategoryTabs && categoryTabs.length > 0 && (
									<Tabs
										aria-label="Dimension categories"
										value={activeCategoryId}
										variant="fullWidth"
										scrollButtons={false}
										sx={{
											flexShrink: 0,
											"& .MuiTab-root": {
												minWidth: 0,
												px: 0.5,
												fontSize: "0.75rem",
												overflowWrap: "anywhere",
											},
										}}
										onChange={(_, categoryId: number) => {
											const gridApi = gridApiRef.current;
											if (gridApi && !gridApi.isDestroyed())
												gridApi.stopEditing();
											setSelectedCategoryId(categoryId);
										}}
									>
										{categoryTabs.map((tab) => (
											<Tab
												key={tab.id}
												value={tab.id}
												label={tab.label}
												wrapped
												disabled={isSaving}
											/>
										))}
									</Tabs>
								)}
								<SectionSpecDimensionsGrid
									onDegreesChange={
										isM1 && !isCorrection
											? (profileItemId, IsDegrees) =>
													updateRow(profileItemId, (row) => ({
														...row,
														IsDegrees,
													}))
											: undefined
									}
									angleInputs={isM1}
									showType={isM1}
									showDegrees={visibleRows.some(isGaugeSpecItem)}
									showBands={
										!isM1 ||
										visibleRows.some((row) => row.CategoryName !== "Samples")
									}
									scrollable
									rows={visibleRows}
									isLoading={false}
									readOnly={isSaving}
									getError={(profileItemId, field: SpecBandField) => {
										const index = rows.findIndex(
											(row) => row.ProfileItemId === profileItemId,
										);
										return index < 0
											? undefined
											: errors.items?.[index]?.[field]?.message;
									}}
									getToggleLock={(row) => {
										const measured = measuredCounts.get(row.ProfileItemId) ?? 0;
										return isCorrection && row.Applies && measured > 0
											? `Measured by ${measured} inspection(s) of this revision, so it cannot be removed. Min, Target and Max can still be edited.`
											: undefined;
									}}
									onGridApiChange={(api) => {
										gridApiRef.current = api;
									}}
									onToggle={(profileItemId, applies) =>
										updateRow(profileItemId, (row) => ({
											...row,
											Applies: applies,
										}))
									}
									onEdit={(profileItemId, field, value) =>
										updateRow(profileItemId, (row) => ({
											...row,
											[field]: value,
										}))
									}
								/>
							</Stack>
						</Grid2>
						<Grid2
							size={{ xs: 12, md: 6 }}
							sx={{ display: "flex", minHeight: 0 }}
						>
							<Stack spacing={0.75} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
								<Typography variant="subtitle2" fontWeight={700}>
									Files
								</Typography>
								<Grid2
									container
									spacing={1}
									sx={{ minHeight: 0, flex: { md: 1 } }}
								>
									<Grid2
										size={{ xs: 12, sm: isM1 ? 12 : 6 }}
										sx={{ minHeight: 0 }}
									>
										<SectionSpecFileSelection
											fillHeight
											disabled={isSaving}
											change={drawingChange}
											label="Select Drawing File"
											rotateLabel="Mill Drawing"
											onChange={setDrawingChange}
											onError={onError}
											persistedFileId={
												initialSpec?.MillDrawingUploadFileId ?? null
											}
											persistedFileName={
												initialSpec?.MillDrawingFileName ?? null
											}
										/>
									</Grid2>
									{!isM1 && (
										<Grid2 size={{ xs: 12, sm: 6 }} sx={{ minHeight: 0 }}>
											<SectionSpecFileSelection
												fillHeight
												disabled={isSaving}
												change={referenceChange}
												label="Select Reference File"
												rotateLabel="Dimensions Reference"
												onChange={setReferenceChange}
												onError={onError}
												persistedFileId={
													initialSpec?.DimensionsReferenceUploadFileId ?? null
												}
												persistedFileName={
													initialSpec?.DimensionsReferenceFileName ?? null
												}
											/>
										</Grid2>
									)}
								</Grid2>
							</Stack>
						</Grid2>
					</Grid2>
				</Stack>
			</MesfModal.Content>
			<MesfModal.Actions sx={{ px: 3, py: 2 }}>
				<Button
					data-test-id="close-spec-button"
					color="inherit"
					disabled={isSaving}
					onClick={onClose}
				>
					Close
				</Button>
				<Button
					variant="contained"
					disabled={isSaving}
					startIcon={
						isSaving ? (
							<CircularProgress size="1rem" color="inherit" />
						) : (
							<SaveIcon />
						)
					}
					data-test-id="save-spec-button"
					onClick={submit}
				>
					Save
				</Button>
			</MesfModal.Actions>
			<ConfirmationDialogRaw
				data-test-id="spec-correction-dialog"
				open={pendingCorrection !== null}
				title="Save Published Revision"
				message={[
					inspectionCount > 0
						? `This revision has ${inspectionCount} associated inspection(s). Their results will be recalculated with the new values.`
						: "This revision has no inspections.",
					displayedEffectiveDate?.isValid() &&
					displayedEffectiveDate.isAfter(moment.utc())
						? "The new Effective Date is in the future: the revision will become scheduled and the previous revision will be in force until then."
						: null,
					"The previous values are kept in the log.",
				]
					.filter(Boolean)
					.join(" ")}
				buttons={{ no: "Cancel", yes: "Save" }}
				onClose={(value) => {
					const values = pendingCorrection;
					setPendingCorrection(null);
					if (value === "OK" && values !== null) saveMutation.mutate(values);
				}}
			/>
		</>
	);
};
